import React from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import news from '../data/news.json'
import teasers from '../data/teasers'
import TeaserShowcase from '../utils/TeaserShowcase'
import Reveal from '../utils/Reveal'
import Fluid from '../utils/Fluid'
import HighlightsRail from '../utils/HighlightsRail'
import ProjectVideo from '../utils/ProjectVideo'
import coralReef from '../resources/teasers/SPEEDLab_CoralReef_Teaser_30s.mp4'

import tai from '../resources/tai.png'
import ctrl_theory from '../resources/ctrl_trajectory.png'
import quantumAI from '../resources/QAI.png'
import fl from '../resources/federated_vlm.png'
import efficientVlm from '../resources/efficient_vlmfixed.png'
import reconstruction from '../resources/Reconstruction.png'

import roadsentry from '../resources/roadsentry.png'
import slade from '../resources/slade.png'
import parrot from '../resources/parrot.png'
import self_correcting from '../resources/self-correcting.png'
import diffusion from '../resources/diffusion.png'

import knife from '../resources/Crimedetection.png'
import flamingo from '../resources/flamingo.png'
import Crimewatchgpt from '../resources/Crimewatchgpt.png'
import drone from '../resources/drone.png'
import traffic from '../resources/traffic.png'
import fedavo from '../resources/fedavo.png'

import neurips_emnlp_2026 from '../resources/neurips_emnlp_2026.jpg'
import cvpr_2026 from '../resources/cvpr_2026.jpeg'
import awal_emnlp from '../resources/awal_emlnp.jpg'
import zarif_siu from '../resources/zarif-siu.jpeg'
import cvpr_2025 from '../resources/cvpr_2025.jpeg'
import rose_llm from '../resources/rose_llm.jpeg'
import toy2 from '../resources/imteaj_toy2.jpeg'
import Imteaj from '../resources/imteaj-head.jpg'
import ahmed from '../resources/ahmed.jpg'
import nash from '../resources/nashville.jpg'
import tapia from '../resources/tapia.jpg'
import realtriumph from '../resources/realtriumph.JPG'
import csci from '../resources/csci.png'
import topres from '../resources/topres.jpg'

import nsf_seal from '../resources/nsf_seal.png'
import dhs_seal from '../resources/dhs_seal.png'
import nairr_pilot from '../resources/NAIRR_pilot_wordmark.png'
import oraut from '../resources/ORAU.png'
import vip from '../resources/vip.jpg'
import access from '../resources/images.jpg'
import agenticAI from '../resources/agenticai.png'
import alignment from '../resources/alignment.png'

// Areas without a diagram (`img`) show their `icon` on a gradient tile.
const researchAreas = [
  { title: 'Robust and Trustworthy AI', img: tai },
  { title: 'Control Theory of LLM/VLM', img: ctrl_theory },
  { title: '3D/4D Reconstruction and Digital Twin', img: reconstruction },
  { title: 'Quantum AI', img: quantumAI },
  { title: 'Federated Learning', img: fl },
  { title: 'Efficient Multimodal AI', img: efficientVlm },
  { title: 'AI Alignment and Safety', img: alignment },
  { title: 'Agentic AI', img: agenticAI },
]

const domains = [
  { icon: 'fa-city', label: 'Smart City Intelligence' },
  { icon: 'fa-house-flood-water', label: 'Disaster Resilience' },
  { icon: 'fa-car-side', label: 'Transportation' },
  { icon: 'fa-water', label: 'Underwater Robotics and Perception' },
  { icon: 'fa-heart-pulse', label: 'Healthcare' },
  { icon: 'fa-seedling', label: 'Agriculture' },
]

const ongoing = [
  {
    title: 'Every Reef Has a Shape: 3D Coral Reef Perception',
    video: coralReef,
    description:
      'In collaboration with I-SMART, we turn boat-based laser scans of coral reefs into dense 3D point clouds and train models to recognize individual coral species, building a living digital map of the reef that supports monitoring and marine conservation.',
  },
  {
    title:
      'RoadSentry: AI-Powered Roadside Construction Safety Intelligence',
    img: roadsentry,
    description:
      'An AI safety platform for roadside work zones that combines computer vision, multi-object tracking and vision-language models to monitor workers, vehicles and equipment, flag hazards and near-misses, and raise real-time alerts. A spatial safety layer reasons about restricted-zone entry, worker–equipment proximity and movement, while a central dashboard surfaces AI-generated incident summaries, risk levels, visual evidence and safety trends.',
  },
  {
    title: 'Developing Robust and Reliable Vision Language Models',
    img: slade,
    description:
      'Vision-language models that accurately interpret visual content and generate meaningful, contextually grounded descriptions, improving both visual understanding and text generation for captioning, visual question answering and other tasks that require deep joint reasoning over images and text.',
  },
  {
    title: 'Making Multimodal LLMs Efficient during Runtime',
    img: parrot,
    description:
      'Token pruning strategies for Large Vision-Language Models that identify and discard redundant visual tokens across the pipeline, cutting compute and memory while preserving visual reasoning and question answering, for deployment on resource-constrained edge devices.',
  },
  {
    title: 'Self-Correcting Multimodal LLMs',
    img: self_correcting,
    description:
      'Techniques that let an LVLM notice when it starts hallucinating and correct itself, producing accurate, grounded responses without external intervention or human feedback, to make LVLMs more reliable in real-world deployment.',
  },
  {
    title: 'Fine-Grained Diffusion for Realistic Image Generation',
    img: diffusion,
    description:
      'Improving diffusion models so that fine-grained semantics in a prompt (object relationships, spatial arrangements, subtle visual cues) are faithfully reflected in the generated image, enabling precise, controllable synthesis for autonomous driving, simulation and scientific visualization.',
  },
]

const additional = [
  {
    title: 'SkyWatch: Criminal Activity Detection via Keypoint Analysis with Drones',
    img: knife,
    description:
      'Drone imagery and keypoint detection to recognize criminal activity patterns from the air.',
  },
  {
    title: 'FLAMINGO: Resilient Federated Meta-Learning against Adversarial Attacks',
    img: flamingo,
    description:
      'A federated meta-learning algorithm that makes personalized edge models robust to adversarial attacks.',
  },
  {
    title: 'WatchOverGPT: Real-Time Crime Detection Framework',
    img: Crimewatchgpt,
    description:
      'Wearable cameras, location data and LLM-based conversational AI to detect and respond to safety threats.',
  },
  {
    title: 'Skyward Insights: Drone-Powered Land Cover Detection',
    img: drone,
    description:
      'Federated learning over drone imagery for real-time classification of land cover patterns.',
  },
  {
    title: 'Driving Smarter Roads: Real-time Traffic Insights',
    img: traffic,
    description:
      'Federated learning and social media analysis for real-time traffic management.',
  },
  {
    title: 'Robust, Lightweight and Secure Distributed ML',
    img: fedavo,
    description:
      'Algorithms that adapt to diverse data distributions while staying lightweight enough for constrained devices.',
  },
]

const highlights = [
  {
    img: awal_emnlp,
    badge: 'Conference',
    text: 'Awal presented our research at EMNLP 2026 in Budapest, Hungary.',
  },
  {
    img: neurips_emnlp_2026,
    badge: 'Publications',
    text: "SPEED Lab research has been accepted to NeurIPS'26 and EMNLP'26!",
    link: 'https://lnkd.in/p/ebuNeW_E',
  },
  {
    img: cvpr_2026,
    badge: 'Conference',
    text: 'Dr. Imteaj presented a poster at IEEE/CVF CVPR 2026.',
  },
  {
    img: zarif_siu,
    badge: 'Achievement',
    text: 'PhD student Zarif received the Doctoral Fellowship from Southern Illinois University.',
  },
  {
    img: cvpr_2025,
    badge: 'Conference',
    text: 'Zarif presented a poster at IEEE/CVF CVPR 2025.',
  },
  {
    img: rose_llm,
    badge: 'Special Session',
    text: 'SPEED Lab hosted ROSE-LLM 2025, the Robustness and Security of LLMs special session at ICMLA 2025 in Boca Raton.',
  },
  {
    img: Imteaj,
    badge: 'Grant',
    text: 'NSF CRII grant of $167,500 awarded to Dr. Imteaj.',
  },
  {
    img: ahmed,
    badge: 'Grant',
    text: 'Dr. Imteaj’s project with FIU won a nearly $100,000 DHS grant.',
  },
  {
    img: topres,
    badge: 'Recognition',
    text: 'Dr. Ahmed Imteaj named in Stanford’s Top 2% Scientists for research impact.',
  },
  {
    img: nash,
    badge: 'Conference',
    text: 'Zarif presented a paper at IEEE SmartComp.',
  },
  {
    img: tapia,
    badge: 'Award',
    text: 'Saika won the Graduate Research Prize at Tapia ’23.',
  },
  {
    img: realtriumph,
    badge: 'Recognition',
    text: 'Dr. Imteaj received the FIU Real Triumph Award.',
  },
  {
    img: csci,
    badge: 'Best Paper',
    text: 'Best Paper Award at the IEEE CSCI conference.',
  },
  { img: toy2, badge: 'Lab Life', text: 'Moments from the SPEED Lab.' },
]

// 2026 publication summary shown in the hero.
const stats2026 = {
  papers: 12,
  coreAStar: 7,
  q1Journals: 2,
  venues: [
    'NeurIPS',
    'EMNLP',
    'CVPR',
    'ACL',
    'IJCNN',
    'ICTAI',
    'Scientific Reports',
    'IEEE TSC',
  ],
}

const sponsors = [
  { img: nsf_seal, name: 'National Science Foundation' },
  { img: dhs_seal, name: 'Department of Homeland Security' },
  { img: nairr_pilot, name: 'NAIRR Pilot' },
  { img: oraut, name: 'ORAU' },
  { img: vip, name: 'FAU VIP' },
  { img: access, name: 'ACCESS' },
]

const SectionHead = ({ eyebrow, title, lead, action }) => (
  <Reveal className='section-head'>
    <div>
      <div className='eyebrow'>{eyebrow}</div>
      <h2 className='section-title'>{title}</h2>
      {lead && <p className='section-lead'>{lead}</p>}
    </div>
    {action}
  </Reveal>
)

const HomeScreen = () => {
  const latestNews = [...news].reverse().slice(0, 3)

  return (
    <div className='home'>
      {/* ------------------------------------------------------------ Hero */}
      <section className='hero'>
        <div className='aurora' aria-hidden='true'>
          <span className='aurora__blob aurora__blob--1' />
          <span className='aurora__blob aurora__blob--2' />
          <span className='aurora__blob aurora__blob--3' />
        </div>
        <Fluid />

        <div className='container-wide hero__inner'>
          <motion.div
            className='eyebrow eyebrow--light hero__eyebrow'
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className='pulse-dot' /> Florida Atlantic University · EECS
          </motion.div>

          <motion.h1
            className='hero__title'
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
          >
            Secure Prediction, Edge AI
            <br />
            &amp; <em>Multimodal LLM</em> Lab
          </motion.h1>

          <motion.p
            className='hero__lead'
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.25, ease: [0.22, 1, 0.36, 1] }}
          >
            Advancing robust, efficient, trustworthy, and controllable AI across
            multimodal intelligence, federated learning, agentic AI,
            digital twins, quantum AI, and cybersecurity.
          </motion.p>

          <motion.div
            className='hero__actions'
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
          >
            <a href='#selected-research' className='btn-pill btn-pill--light'>
              Selected research <i className='fa-solid fa-arrow-down' />
            </a>
            <Link to='/publications' className='btn-pill btn-pill--ghost'>
              Publications
            </Link>
            <Link to='/team' className='btn-pill btn-pill--ghost'>
              Meet the team
            </Link>
          </motion.div>

          <motion.div
            className='hero__venues'
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.7 }}
          >
            <span className='hero__venues-label'>2026 at top AI venues</span>
            <div className='venue-card'>
              <div className='venue-card__count'>{stats2026.papers}</div>
              <div className='venue-card__body'>
                <p className='venue-card__summary'>
                  <strong>SPEED Lab papers in 2026</strong>, {stats2026.coreAStar}{' '}
                  of them at <span className='venue-card__core'>CORE A*</span>{' '}
                  venues and {stats2026.q1Journals} in{' '}
                  <span className='venue-card__q1'>Q1 journals</span>, across
                </p>
                <ul className='venue-card__list'>
                  {stats2026.venues.map((v, i) => (
                    <li key={v} className={`venue-card__venue venue-c${i}`}>
                      {v}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ------------------------------------------------ Selected research */}
      <section className='section' id='selected-research'>
        <div className='container-wide container-wide--xl'>
          <SectionHead
            eyebrow='Selected Research'
            title={
              <>
                Our latest work, <em>in motion</em>
              </>
            }
          />
          <Reveal delay={0.1}>
            <TeaserShowcase teasers={teasers} />
          </Reveal>
        </div>
      </section>

      {/* --------------------------------------------------- Research areas */}
      <section className='section section--tint'>
        <div className='container-wide'>
          <SectionHead
            eyebrow='Research Areas'
            title='What we work on'
            lead='Our research spans the full lifecycle of multimodal, secure and efficient AI, from foundations to deployment at the edge.'
          />
          <div className='area-grid'>
            {researchAreas.map((a, i) => (
              <Reveal key={a.title} delay={i * 0.06} className='area-card'>
                {a.img ? (
                  <div className='area-card__media'>
                    <img src={a.img} alt='' />
                  </div>
                ) : (
                  <div className='area-card__media area-card__media--icon'>
                    <i className={`fa-solid ${a.icon}`} />
                  </div>
                )}
                <div className='area-card__body'>
                  <span className='area-card__index'>{String(i + 1).padStart(2, '0')}</span>
                  <h3>{a.title}</h3>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* -------------------------------------------------------- Domains */}
      <section className='section domains'>
        <div className='aurora aurora--subtle' aria-hidden='true'>
          <span className='aurora__blob aurora__blob--2' />
        </div>
        <div className='container-wide domains__inner'>
          <Reveal>
            <div className='eyebrow eyebrow--light'>Application Domains</div>
            <h2 className='domains__title'>
              Where our research <em>makes an impact</em>
            </h2>
          </Reveal>
          <div className='domains__grid'>
            {domains.map((d, i) => (
              <Reveal key={d.label} delay={i * 0.05} className='domain'>
                <i className={`fa-solid ${d.icon}`} />
                <span>{d.label}</span>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ------------------------------------------------- Ongoing research */}
      <section className='section'>
        <div className='container-wide'>
          <SectionHead
            eyebrow='Ongoing Research'
            title='Projects in progress'
            lead='Active projects across vision-language models, generative AI and safety-critical applications.'
          />
          <div className='project-list'>
            {ongoing.map((p, i) => (
              <Reveal key={p.title} className={`project-row ${i % 2 ? 'is-flipped' : ''}`}>
                <div className={`project-row__media ${p.video ? 'project-row__media--video' : ''}`}>
                  {p.video ? (
                    <ProjectVideo src={p.video} title={p.title} />
                  ) : (
                    <img src={p.img} alt={p.title} />
                  )}
                </div>
                <div className='project-row__body'>
                  <span className='status-pill'>
                    <span className='pulse-dot pulse-dot--green' /> Ongoing
                  </span>
                  <h3>{p.title}</h3>
                  <p>{p.description}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal className='subhead'>
            <h3>More projects</h3>
          </Reveal>
          <div className='mini-grid'>
            {additional.map((p, i) => (
              <Reveal key={p.title} delay={(i % 3) * 0.06} className='mini-card'>
                <div className='mini-card__media'>
                  <img src={p.img} alt='' />
                </div>
                <div className='mini-card__body'>
                  <h4>{p.title}</h4>
                  <p>{p.description}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ------------------------------------------------------- Highlights */}
      <section className='section section--tint'>
        <HighlightsRail
          items={highlights}
          header={(controls) => (
            <div className='container-wide'>
              <SectionHead
                eyebrow='Highlights'
                title='Milestones & recognition'
                action={controls}
              />
            </div>
          )}
        />
      </section>

      {/* ------------------------------------------------------------- News */}
      <section className='section'>
        <div className='container-wide'>
          <SectionHead
            eyebrow='News'
            title='Latest from the lab'
            action={
              <Link to='/news' className='text-link'>
                All news <i className='fa-solid fa-arrow-right' />
              </Link>
            }
          />
          <div className='news-grid'>
            {latestNews.map((n, i) => (
              <Reveal key={n.id} delay={i * 0.06}>
                <Link to={`/news/${n.id}`} className='news-tile'>
                  <span className='news-tile__meta'>
                    {n.type}
                    {n.date && <> · {n.date}</>}
                  </span>
                  <h3>{n.title}</h3>
                  <span className='news-tile__more'>
                    Read more <i className='fa-solid fa-arrow-right' />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* --------------------------------------------------------- Sponsors */}
      <section className='sponsors'>
        <div className='container-wide'>
          <p className='sponsors__label'>Supported by</p>
          <ul className='sponsors__grid'>
            {sponsors.map((s) => (
              <li className='sponsor' key={s.name} title={s.name}>
                <img src={s.img} alt={s.name} loading='lazy' />
              </li>
            ))}
          </ul>
        </div>
      </section>
    </div>
  )
}

export default HomeScreen
